import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { IconX } from '@tabler/icons-react';

import IconButton from './IconButton';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** `right` slides from the right edge (web default). `bottom` slides up (mobile sheet). */
  position?: 'right' | 'bottom';
  title?: string;
  children: React.ReactNode;
  /** When true, hides the visible title bar (still announced to screen readers). */
  hideHeader?: boolean;
}

/**
 * Accessible Drawer/Sheet wrapping `@radix-ui/react-dialog`.
 * Radix gives us focus trap, scroll lock, escape-to-close, and ARIA roles.
 * Visuals are our own — see `.rr-drawer*` in components.css.
 */
const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  position = 'right',
  title,
  hideHeader = false,
  children,
}) => (
  <Dialog.Root open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
    <Dialog.Portal>
      <Dialog.Overlay className="rr-overlay" />
      <Dialog.Content
        className={['rr-drawer', position === 'bottom' ? 'rr-drawer-bottom' : '']
          .filter(Boolean)
          .join(' ')}
      >
        <div className="rr-drawer-handle" aria-hidden="true">
          <div className="rr-drawer-handle-bar" />
        </div>
        {hideHeader ? (
          <Dialog.Title className="rr-drawer-title-hidden">{title ?? 'Panel'}</Dialog.Title>
        ) : (
          <div className="rr-drawer-header">
            <Dialog.Title className="rr-drawer-title">{title ?? ''}</Dialog.Title>
            <Dialog.Close asChild>
              <IconButton icon={<IconX size={18} />} aria-label="Close" />
            </Dialog.Close>
          </div>
        )}
        <div className="rr-drawer-body">{children}</div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);

export default Drawer;
